/**
 * Coles Credit Card CSV Parser (pure, browser-safe).
 *
 * Coles (issued via NAB) exports a HEADERED CSV — unlike the headerless
 * ANZ format — with these columns:
 *
 *   Date, Amount, Account Number, <blank>, Transaction Type,
 *   Transaction Details, Category, Merchant Name, Processed On
 *
 * Real-sample quirks this parser handles:
 *   - Dates are 'd MMM yy' with INCONSISTENT month tokens: '27 May 26',
 *     '13 Sept 25' (4 letters), '11 July 25' (full name).
 *   - The 4th column is unnamed and always empty.
 *   - 'Account Number' alternates between 'Card ending 2607' (purchases)
 *     and the full account number (fees/interest/payments).
 *   - Amount is signed decimal; negative = spend, positive = payment
 *     received onto the card.
 *
 * Fields are split with a quote-aware state machine (RFC-4180-ish) so a
 *  merchant name containing a comma can't shear the row.
 */

export interface ParsedColesRow {
  /** ISO YYYY-MM-DD (transaction date). */
  date: string;
  /** Signed AUD amount. Negative = spend. */
  amount: number;
  /** Transaction Details column, whitespace-normalised — the analogue
   *  of ANZ's description; used for rules + dedupe hashing. */
  description: string;
  /** Transaction Type column, e.g. 'CREDIT CARD PURCHASE', 'FEES',
   *  'INTEREST CHARGED', 'CREDIT CARD PAYMENT'. */
  txType: string;
  /** Bank's own category column (e.g. 'Uncategorised', 'Fees'). Kept
   *  as a hint only — our rules engine is authoritative. */
  bankCategory: string;
  /** Merchant Name column when the bank resolved one (often blank). */
  merchantName: string;
  /** ISO YYYY-MM-DD settlement date (Processed On), when parseable. */
  processedOn?: string;
}

export interface ColesParseResult {
  rows: ParsedColesRow[];
  errors: Array<{ line: number; text: string; reason: string }>;
}

/** Month token → 01-12. Keys checked by 3-letter prefix AND full token
 *  so 'Sep', 'Sept', 'September', 'July' all resolve. */
const MONTHS: Record<string, string> = {
  jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06',
  jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12',
};

/** Convert '13 Sept 25' / '11 July 25' / '27 May 26' → ISO. Returns
 *  null when the shape doesn't match. Two-digit years are 20yy (this
 *  product didn't exist before 2000; revisit in 2099). */
export function parseColesDate(s: string): string | null {
  const m = s.trim().match(/^(\d{1,2})\s+([A-Za-z]+)\.?\s+(\d{2}|\d{4})$/);
  if (!m) return null;
  const day = parseInt(m[1], 10);
  if (day < 1 || day > 31) return null;
  const month = MONTHS[m[2].toLowerCase().slice(0, 3)];
  if (!month) return null;
  const year = m[3].length === 2 ? `20${m[3]}` : m[3];
  return `${year}-${month}-${String(day).padStart(2, '0')}`;
}

/** Quote-aware CSV line splitter (handles "a, b",c and "" escapes). */
function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++; } // escaped quote
        else inQuotes = false;
      } else cur += ch;
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      out.push(cur);
      cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out;
}

/** Sniff: does this text look like a Coles credit-card export? Checks
 *  the first non-empty line for the known header columns. */
export function looksLikeColesCsv(text: string): boolean {
  const first = (text.split(/\r?\n/).find((l) => l.trim()) || '').toLowerCase();
  return first.startsWith('date,amount,account number')
    && first.includes('transaction details');
}

/** Parse a full Coles CC CSV (as text). Skips the header row; tolerates
 *  blank lines. Returns rows in file order. */
export function parseColesCsv(text: string): ColesParseResult {
  const rows: ParsedColesRow[] = [];
  const errors: Array<{ line: number; text: string; reason: string }> = [];
  const lines = text.split(/\r?\n/);

  let headerSeen = false;
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const line = raw.trim();
    if (!line) continue;
    if (!headerSeen) {
      // First non-empty line is the header — validated by the caller's
      // looksLikeColesCsv sniff; skip it here regardless.
      headerSeen = true;
      continue;
    }

    const cols = splitCsvLine(line);
    if (cols.length < 6) {
      errors.push({ line: i + 1, text: raw, reason: `Expected ≥6 columns, got ${cols.length}` });
      continue;
    }

    const date = parseColesDate(cols[0]);
    if (!date) {
      errors.push({ line: i + 1, text: raw, reason: `Invalid date '${cols[0]}' (expected 'd MMM yy')` });
      continue;
    }

    const amount = Number(String(cols[1]).replace(/,/g, '').trim());
    if (!Number.isFinite(amount)) {
      errors.push({ line: i + 1, text: raw, reason: `Invalid amount '${cols[1]}'` });
      continue;
    }

    const description = String(cols[5] || '').trim().replace(/\s+/g, ' ');
    if (!description) {
      errors.push({ line: i + 1, text: raw, reason: 'Empty Transaction Details' });
      continue;
    }

    rows.push({
      date,
      amount,
      description,
      txType: String(cols[4] || '').trim(),
      bankCategory: String(cols[6] || '').trim(),
      merchantName: String(cols[7] || '').trim(),
      processedOn: parseColesDate(String(cols[8] || '')) || undefined,
    });
  }

  return { rows, errors };
}
